import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Mode } from 'src/@types/mode';
import { Role } from 'src/@types/role';
import DefaultHomeRoute from 'src/layouts/app/nav/DefaultHomeRoute';
import { useCurrentOrgContext } from 'src/contexts/OrgContext';
import { SettingsContext } from 'src/components/settings/utils';

/**
 * Moves this tab into another org: drops the outgoing org's cache, then moves
 * the org context and the brand palette together.
 *
 * The switcher and useSessionTabSync both run this, so a tab that learns about
 * a switch from a sibling ends up in the same state as the tab that made it.
 * Writing the localStorage keys that wake those siblings belongs to the
 * switcher alone, and so does the route, because only the switcher knows the
 * new org's role without waiting for a refetch.
 */
export default function useApplyOrgSwitch() {
  const queryClient = useQueryClient();
  const { setOrgId, setMode } = useCurrentOrgContext();
  const { changeTheme } = useContext(SettingsContext);

  return useCallback(
    (orgId: string, mode: Mode) => {
      // Every cached result belongs to the org being left. Cancelling settles
      // each in-flight request's retryer promise, so a response already
      // carrying the old x-org-id header is discarded instead of landing in
      // the cache after it is cleared. It does not abort the request: React
      // Query cancellation is cooperative and useAxios hands its query
      // function no signal, so the request still reaches the server.
      queryClient.cancelQueries();
      queryClient.clear();

      setOrgId(orgId);
      setMode(mode);
      changeTheme(mode === 'dt' ? 'datatrust' : 'default');
    },
    [changeTheme, queryClient, setMode, setOrgId]
  );
}

/**
 * Lands the tab on a role's home route. Both org-switch paths read the route
 * map through here, so the destination cannot drift between them.
 */
export function useNavigateOrgHome() {
  const navigate = useNavigate();

  return useCallback(
    (role: Role | undefined) => navigate(DefaultHomeRoute[role as Role]),
    [navigate]
  );
}
